import "./dashboard-shell.css";
import { renderBrandIcon } from "./BrandIcon.js";
import { escapeHtml } from "./escapeHtml.js";

export type DashboardPage =
  | "home"
  | "roster"
  | "academy"
  | "barracks"
  | "hospital"
  | "warehouse"
  | "technique-archive";

const DASHBOARD_PAGES: ReadonlySet<string> = new Set([
  "home",
  "roster",
  "academy",
  "barracks",
  "hospital",
  "warehouse",
  "technique-archive",
]);

const VILLAGE_PAGES: ReadonlyArray<{
  id: Exclude<DashboardPage, "home">;
  icon: string;
  label: string;
}> = [
  { id: "roster", icon: "&#x265F;", label: "Ninja roster" },
  { id: "academy", icon: "&#x2726;", label: "Academy" },
  { id: "barracks", icon: "&#x265C;", label: "Barracks" },
  { id: "hospital", icon: "&#x2692;", label: "Hospital" },
  { id: "warehouse", icon: "&#x25A6;", label: "Warehouse" },
  {
    id: "technique-archive",
    icon: "&#x8853;",
    label: "Technique Archive",
  },
];

export interface DashboardShellOptions {
  username: string;
  ninjaCount: number;
  academyFoundationRemaining: number;
  selectedPage: DashboardPage;
  onLogout: () => void;
  onPageSelect: (page: DashboardPage) => void;
}

export function mountDashboardShell(
  root: HTMLElement,
  options: DashboardShellOptions,
): HTMLElement {
  const activeVillagePage = VILLAGE_PAGES.find(
    (page) => page.id === options.selectedPage,
  );
  const villageLinks = VILLAGE_PAGES.map((page) => {
    const trailingMarkup =
      page.id === "roster"
        ? `<span class="navigation-count">${options.ninjaCount}</span>`
        : page.id === "academy" && options.academyFoundationRemaining > 0
          ? '<i class="navigation-dot" aria-label="Pending recruitment"></i>'
          : "";
    return renderNavigationLink(
      page.icon,
      page.label,
      page.id,
      options.selectedPage,
      trailingMarkup,
    );
  }).join("");

  root.innerHTML = `
    <div class="dashboard-shell">
      <header class="topbar">
        <a class="wordmark" href="#home" data-navigation-page="home" aria-label="Shinobi Idle field operations"><span class="wordmark-icon-shell" aria-hidden="true">${renderBrandIcon({ size: "large", className: "wordmark-brand-icon" })}</span> SHINOBI IDLE</a>
        <nav class="primary-navigation" aria-label="Main navigation">
          <button type="button" data-navigation-page="home" class="top-navigation-link${options.selectedPage === "home" ? " selected" : ""}" aria-label="Field Operations" title="Field Operations"${options.selectedPage === "home" ? ' aria-current="page"' : ""}><img class="dashboard-navigation-image" src="/assets/interface/field-operations.webp" alt="" width="32" height="32" /><span class="navigation-label">Field Operations</span></button>
          <details class="village-navigation${activeVillagePage ? " has-active-page" : ""}">
            <summary aria-label="Village navigation" title="Village"><img class="dashboard-navigation-image" src="/assets/interface/village-gate.webp" alt="" width="32" height="32" /><span class="navigation-label">Village</span>${activeVillagePage ? `<small>${escapeHtml(activeVillagePage.label)}</small>` : ""}<span class="navigation-caret" aria-hidden="true"></span></summary>
            <nav class="village-navigation-panel" aria-label="Village pages">${villageLinks}</nav>
          </details>
        </nav>
        <div class="topbar-right">
          <span class="status-pill"><i aria-hidden="true"></i> SERVER CONNECTED</span>
          <span class="user-chip" title="${escapeHtml(options.username)}">${escapeHtml(options.username)}</span>
          <button id="logout" class="button button-quiet" type="button">Sign out</button>
        </div>
      </header>
      <div class="dashboard-grid">
        <main id="content" class="content-area"></main>
      </div>
    </div>`;

  const shell = root.querySelector<HTMLElement>(".dashboard-shell");
  const content = shell?.querySelector<HTMLElement>("#content");
  if (!shell || !content) throw new Error("Dashboard shell failed to mount");

  shell.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const navigationLink = target.closest<HTMLElement>(
      "[data-navigation-page]",
    );
    if (navigationLink && shell.contains(navigationLink)) {
      event.preventDefault();
      const page = navigationLink.dataset.navigationPage;
      if (page && DASHBOARD_PAGES.has(page)) {
        navigationLink.closest("details")?.removeAttribute("open");
        options.onPageSelect(page as DashboardPage);
      }
      return;
    }

    const button = target.closest<HTMLButtonElement>("button");
    if (button?.id === "logout" && shell.contains(button)) options.onLogout();
  });

  return content;
}

function renderNavigationLink(
  icon: string,
  label: string,
  page: DashboardPage,
  selectedPage: DashboardPage,
  trailingMarkup = "",
): string {
  const selected = page === selectedPage;
  return `<button type="button" data-navigation-page="${page}" class="village-navigation-link${selected ? " selected" : ""}"${selected ? ' aria-current="page"' : ""}><span class="navigation-icon" aria-hidden="true">${icon}</span><span>${label}</span>${trailingMarkup}</button>`;
}
